import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ItemVisual } from '../components/ItemVisual';
import { audio } from '../core/audio';
import { recordSkill } from '../core/storage';

// Generic drag-to-target: Colour Baskets + Sort the World
export function CategorySort({ activity, onComplete }) {
  const [placed, setPlaced] = useState({});
  const [dragging, setDragging] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const targetRefs = useRef({});
  const stats = useRef({ attempts: 0, hints: 0 });

  useEffect(() => { const t = setTimeout(() => audio.playVoice(activity.voice), 400); return () => clearTimeout(t); }, [activity.id, activity.voice]);

  const down = (idx) => (e) => {
    if (placed[idx]) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    audio.playSfx('pickup');
    setDragging(idx);
    setPos({ x: e.clientX, y: e.clientY });
  };
  const move = (e) => { if (dragging !== null) setPos({ x: e.clientX, y: e.clientY }); };
  const up = async (e) => {
    if (dragging === null) return;
    const entry = activity.items[dragging];
    stats.current.attempts += 1;
    let hit = null;
    for (const [tid, el] of Object.entries(targetRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (e.clientX >= r.left - 24 && e.clientX <= r.right + 24 && e.clientY >= r.top - 24 && e.clientY <= r.bottom + 24) hit = tid;
    }
    if (hit === entry.target) {
      audio.playSfx('drop');
      recordSkill(activity.skill, true);
      const np = { ...placed, [dragging]: true };
      setPlaced(np);
      if (Object.keys(np).length === activity.items.length) {
        audio.playSfx('correct');
        await audio.playVoice('correct2');
        onComplete({ correct: true, attempts: stats.current.attempts, hints: stats.current.hints });
      }
    } else if (hit) {
      audio.playSfx('retry');
      recordSkill(activity.skill, false);
      audio.playVoice('retry2');
    }
    setDragging(null);
  };

  return (
    <div className="flex-1 flex flex-col justify-between px-4 pb-6 pt-2 touch-none select-none"
      onPointerMove={move} onPointerUp={up} data-testid="activity-category-sort">
      <div className="flex justify-center gap-4 flex-wrap pt-3">
        {activity.items.map((entry, idx) => !placed[idx] && (
          <div key={idx} data-testid={`drag-item-${entry.item.id}-${idx}`} onPointerDown={down(idx)}
            className="cursor-grab active:cursor-grabbing rounded-[1.6rem] p-2 bg-white/75 backdrop-blur"
            style={{ border: '4px solid rgba(255,255,255,0.9)', boxShadow: '0 8px 20px rgba(29,53,87,0.15)', opacity: dragging === idx ? 0.25 : 1 }}>
            <ItemVisual item={entry.item} size={78} />
          </div>
        ))}
      </div>
      {dragging !== null && (
        <div className="fixed z-50 pointer-events-none" style={{ left: pos.x - 44, top: pos.y - 44 }}>
          <ItemVisual item={activity.items[dragging].item} size={88} />
        </div>
      )}
      <div className="flex justify-center gap-4 flex-wrap">
        {activity.targets.map((tg) => {
          const count = activity.items.filter((it, i) => placed[i] && it.target === tg.id).length;
          return (
            <div key={tg.id} ref={(el) => (targetRefs.current[tg.id] = el)} data-testid={`target-${tg.id}`} className="relative">
              <ItemVisual item={tg.visual} size={100} />
              {count > 0 && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                  className="absolute -top-3 -right-2 w-9 h-9 rounded-full bg-leaf text-navy font-heading font-bold flex items-center justify-center shadow">{count}</motion.div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
